import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { ArcAngelSharedService } from './arc-angel-shared.service';
import { ArcAngelService } from './arc-angel.service';
import { CoreService } from './core.service';
import { DataContextService } from './data-context.service';
import { TimelineService } from './timeline.service';
import { IllumLiveService } from './illum-live.service';
import { GlobeService } from './globe.service';
import * as Cesium from 'cesium';
import { IncidentVisualizerComponent } from '../../components/shared/incident-visualizer/incident-visualizer.component';
import { FloorService } from './floor.service';
import { CoresService } from './cores.service';
import { ArcAngelMonitorModeService } from '../../components/shared/arc-angel-monitor-mode.service';
import { DeviceService } from './device.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ResponseModalComponent } from '../../components/shared/response-modal/response-modal.component';
import { WindowService } from './window.service';

@Injectable({
  providedIn: 'root'
})
export class ArcAngelPlaybackModeService {
  // Data state
  data = {
    playbackIncident: undefined as any,
    playbackProcall: undefined as any,
    playbackCalendar: undefined as any,
    playbackTracking: undefined as any
  };
  procallDevices: any;
  private tickCount = 0;
  private lastPullTimestamp: number | undefined;
  private incidentVisualizerRef: ComponentRef<IncidentVisualizerComponent> | null = null;
  private incidentVisualizerCloseSub: Subscription | null = null;

  constructor(
    private core: CoresService,
    private arcAngelShared: ArcAngelSharedService,
    private dataContext: DataContextService,
    private arcAngel: ArcAngelService,
    private illumLiveService: IllumLiveService,
    private timeline: TimelineService,
    private globeService: GlobeService,
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    private floorService: FloorService,
    private monitorModeService: ArcAngelMonitorModeService,
    private deviceService: DeviceService,
    private modalService: NgbModal,
    private windowService: WindowService
  ) {}

  async startIncidentPlaybackMode(eventId: string, scope: any): Promise<void> {
    
    this.monitorModeService.stopMonitorMode();
    this.windowService.closeAllMapEntityDetailsWindow();

    if (!eventId || eventId === 'undefined') {
      console.error('[startIncidentPlaybackMode] Invalid eventId:', eventId);
      throw new Error('Invalid eventId provided for playback mode');
    }
    this.clearPlaybackMode();
      const response = await this.illumLiveService.getPlaybackIncident(eventId).toPromise();
      await this.parsePlaybackEvent(eventId, response);
      const incidentHeight = this.data.playbackIncident.incidentPerimeter 
        ? this.data.playbackIncident.incidentPerimeter.height 
        : 1;
      if (this.data.playbackIncident?.cesiumEntity?.polygon) {
        this.data.playbackIncident.cesiumEntity.polygon.height = incidentHeight;
        this.data.playbackIncident.cesiumEntity.polygon.perPositionHeight = false;
      }
      if (this.data.playbackIncident.outline?.getAltitude && 
          this.data.playbackIncident.outline.getAltitude() !== incidentHeight) {
        this.data.playbackIncident.outline.setAltitude(incidentHeight);
      }
      this.launchPlaybackUi(scope);
      
  }
 private closeIncidentVisualizer(): void {
  if (this.incidentVisualizerRef) {
    // Unsubscribe from any subscriptions to prevent memory leaks
    if (this.incidentVisualizerCloseSub && !this.incidentVisualizerCloseSub.closed) {
      this.incidentVisualizerCloseSub.unsubscribe();
    }
    this.incidentVisualizerCloseSub = null;
    // Remove from DOM and destroy the component
    this.appRef.detachView(this.incidentVisualizerRef.hostView);
    this.incidentVisualizerRef.destroy();
    this.incidentVisualizerRef = null;
    this.floorService.updateIncidentVisualizer(false);
  }
}

// Also add this public method so it can be called from outside
public closeIncidentVisualizerFromOutside(): void {
  this.closeIncidentVisualizer();
}

  clearPlaybackMode(): void {
        this.closeIncidentVisualizer();
    if (this.data.playbackIncident) {
      if (this.data.playbackIncident.buildingMapEntity && 
          typeof this.data.playbackIncident.buildingMapEntity.resetPerimeterSafetyStatus === 'function') {
        this.data.playbackIncident.buildingMapEntity.resetPerimeterSafetyStatus();
      }
      
      // Remove playback devices (you'll need to implement this in your UserService equivalent)
      // if (this.arcAngel && typeof this.arcAngel.removePlaybackDevices === 'function') {
      //   this.arcAngel.removePlaybackDevices(this.data.playbackIncident);
      // }
      
      // Remove from map
      try {
        this.dataContext.removeMapEntity(this.data.playbackIncident);
      } catch (error) {
      }
      
      if (this.arcAngelShared && typeof this.arcAngelShared.clearIncident === 'function') {
        this.arcAngelShared.clearIncident(this.data.playbackIncident);
      }
      
      this.data.playbackIncident = undefined;
    }
    
    if (this.data.playbackProcall) {
      if (typeof this.removeProcallPlaybackDevice === 'function') {
        this.removeProcallPlaybackDevice(this.data.playbackProcall);
      }
      this.data.playbackProcall = undefined;
    }
    
    if (this.data.playbackCalendar) {
      this.data.playbackCalendar = undefined;
    }
    
    if (this.data.playbackTracking) {
      
      // Remove active tracking event (you'll need to implement this)
      // if (this.arcAngel && typeof this.arcAngel.removeActiveTrackingEvent === 'function') {
      //   this.arcAngel.removeActiveTrackingEvent(this.data.playbackTracking);
      // }
      
      this.data.playbackTracking = undefined;
    }

    // Remove tick listeners
    if (this.core && typeof this.core.removeOnTickListener === 'function') {
      this.core.removeOnTickListener('playback-time-listener');
    }
    
    // Reset counters
    this.tickCount = 0;
    this.lastPullTimestamp = undefined;
  }

  private async parsePlaybackEvent(eventId: string, response: any): Promise<void> {
    // 1. Get the latest incident by updatedAt
    const items = response?.items || response?.data?.items || [];
    if (!items.length) {
      return;
    }

    const incident = items.sort((a: any, b: any) =>
      Date.parse(a.updatedAt) - Date.parse(b.updatedAt)
    )[items.length - 1];
    
    if (!eventId || eventId === 'undefined') {
      console.error('[parsePlaybackEvent] Invalid eventId:', eventId);
      return;
    }
    incident.eventId = eventId;
    incident.id = incident.id || eventId;

    const incidentEntity = this.arcAngelShared.parseIncident(incident, eventId, true);
    
    if (!incidentEntity) {
      return;
    }

    incidentEntity.eventId = eventId;
    if (!incidentEntity.id || incidentEntity.id.includes('undefined')) {
      incidentEntity.id = `arc-angel-playback-${eventId}`;
    }

    const possibleIds = [
      `arc-angel-playback-${eventId}`,
      `playback-incident-${eventId}`,
      `incident-${eventId}`,
      eventId
    ];

    possibleIds.forEach(id => {
      try {
        this.dataContext.removeMapEntity(id);
      } catch (error) {
      
      }
    });

    try {
      const viewer = this.globeService.getViewer();
      if (viewer && viewer.entities) {
        possibleIds.forEach(id => {
          const existingEntity = viewer.entities.getById(id);
          if (existingEntity) {
            viewer.entities.remove(existingEntity);
          }
        });
      }
    } catch (error) {
    }

    incidentEntity.incidentPerimeter = incident.incidentPerimeter;
    this.data.playbackIncident = incidentEntity;

    if (incident.affectedPerimeters && incident.affectedPerimeters.length > 0) {
      try {
        const perimeters = this.arcAngel.getAffectedPerimeterEntities(incident.affectedPerimeters);
        if (perimeters && perimeters.length > 0) {
          this.data.playbackIncident.setAffectedPerimeters(perimeters);
        }
      } catch (error) {
        console.error('[parsePlaybackEvent] Error setting affected perimeters:', error);
      }
    }
 this.arcAngel.setBuildingIncident(incidentEntity);
    
    if (incident.updates && Array.isArray(incident.updates)) {
      for (const update of incident.updates) {
        try {
          const updateEntity = this.arcAngelShared.parseIncidentUpdate(update, incident, true);
          if (updateEntity) {
            this.data.playbackIncident.addUpdate(updateEntity);
            this.timeline.addItemEventToTimeline(updateEntity, '', '', 0, 0);
          }
        } catch (error) {
          console.error('[parsePlaybackEvent] Error processing update:', error);
        }
      }
    }

    if (incident.safezones && Array.isArray(incident.safezones)) {
      for (const safezone of incident.safezones) {
        if (safezone.isActive === false || safezone.isActive === 'false' || safezone.active === false || safezone.active === 'false') {
          continue;
        }
        try {
          const safeZoneEntity = this.arcAngelShared.parseIncidentSafezone(safezone, incident, true);
          if (safeZoneEntity) {
            this.data.playbackIncident.addSafeZone(safeZoneEntity);
            this.timeline.addItemEventToTimeline(safeZoneEntity, '', '', 0, 0);
          }
        } catch (error) {
          console.error('[parsePlaybackEvent] Error processing safezone:', error);
        }
      }
    }

    try {
      this.dataContext.addMapEntity(this.data.playbackIncident);
    } catch (error) {
      console.error('[parsePlaybackEvent] Error adding to map:', error);
      throw error;
    }

    this.startPlaybackTickListeners();
  }
    
private startPlaybackTickListeners(): void {
  this.tickCount = 0;
  this.core?.addOnTickListener('playback-time-listener', (clock: any) => {
    try {
      if (this.tickCount % 30 === 0 && this.data.playbackIncident) {
        if (this.data.playbackIncident) {
          let startTimestamp = Math.floor(Cesium.JulianDate.toDate(clock.currentTime).getTime() / 1000) * 1000;
         this.getDataForPlayback(startTimestamp);
          this.arcAngel.updatePerimeterSafetyStatus(this.data.playbackIncident.eventId, startTimestamp);
          if (this.data.playbackIncident.devices) {
            Object.values(this.data.playbackIncident.devices).forEach((device: any) => {
              if (device.setIcon && device.getIconFromStatus && device.getSafetyStatus) {
                device.setIcon(device.getIconFromStatus(device.getSafetyStatus(clock.currentTime)));
              }
            });
          }
        }
      }
      this.tickCount++;
    } catch (err) {
      console.error('[TickListener] Error in tick listener:', err);
    }
  });
}
  stopPlaybackMode() {
    this.stopPlaybackTickListeners();
    this.clearPlaybackMode();
  }
  public stopPlaybackTickListeners() {
    this.core?.removeOnTickListener('playback-time-listener');
  }
  private getDataForPlayback(startTimestamp: number): void {
    if (this.lastPullTimestamp && startTimestamp === this.lastPullTimestamp) {
      return;
    }
    if (typeof this.arcAngel.getUserStatusByPerimeterForPlaybackMulti === 'function') {
      this.arcAngel.getUserStatusByPerimeterForPlaybackMulti(
        this.data.playbackIncident.eventId,
        startTimestamp
      ).subscribe({
        next: (results) => {
          if (results) {
            this.lastPullTimestamp = startTimestamp;
          }
        },
        error: (error) => {
          console.error(error);
        }
      });
    }
  }

  private launchPlaybackUi(scope: any): void {
    if (!this.data.playbackIncident.isBuildingIncident()) {
      this.globeService.getViewer()?.camera.switchToPerspectiveFrustum();
      // Optionally: this.data.playbackIncident.goToEntity();
      if (this.data.playbackIncident.locations) {
        const rectangle = this.getRectangleFromLocations(this.data.playbackIncident.locations);
        this.goToAdHocRegion(rectangle);
      }
      this.openIncidentVisualizer(this.data.playbackIncident, this.data.playbackIncident);
    } else {
      this.globeService.getViewer()?.camera.switchToPerspectiveFrustum();
      this.data.playbackIncident.viewDetailsWindow(scope);
   }
  
    let dateStart1 = new Date();
    if (
      this.data.playbackIncident.dataTimeSlices &&
      this.data.playbackIncident.dataTimeSlices._times &&
      this.data.playbackIncident.dataTimeSlices._times[0]
    ) {
      dateStart1 = Cesium.JulianDate.toDate(
        this.data.playbackIncident.dataTimeSlices._times[0]
      );
    } else {
      dateStart1 = new Date(this.data.playbackIncident.obj.createdAt);
    }
    const dateStart = new Date(new Date(dateStart1).getTime() + 1000);
    if (this.timeline && typeof this.timeline.setTime === 'function') {
      this.timeline.setTime(dateStart);
    }
    if (this.timeline && typeof this.timeline.pause === 'function') {
      this.timeline.pause();
    }
  }

  private openIncidentVisualizer(incident: any, arcLiveEntity?: any): void {
    try {
      this.closeIncidentVisualizer();
      this.incidentVisualizerRef = createComponent(IncidentVisualizerComponent, {
        environmentInjector: this.environmentInjector
      });
      this.incidentVisualizerRef.instance.incident = incident;
      this.incidentVisualizerRef.instance.isPlaybackMode = true;
      if (arcLiveEntity) {
        this.incidentVisualizerRef.instance.mapentityid = arcLiveEntity;
      }
      this.incidentVisualizerCloseSub = this.incidentVisualizerRef.instance.closeRequested.subscribe(() => {
        this.closeIncidentVisualizer();
      });

      this.incidentVisualizerRef.changeDetectorRef.detectChanges();
      document.body.appendChild(this.incidentVisualizerRef.location.nativeElement);
      this.floorService.updateStreetViewData(arcLiveEntity);
      this.floorService.updateIncidentVisualizer(true);
      this.appRef.attachView(this.incidentVisualizerRef.hostView);

    } catch (error) {
      console.error('[openIncidentVisualizer] Error opening incident visualizer:', error);
    }
  }

  private getRectangleFromLocations(locations: any[]): any {
    if (!locations || locations.length === 0) {
      throw new Error('No locations provided for rectangle calculation.');
    }
    const lats: number[] = [];
    const lons: number[] = [];
    locations.forEach((loc: any) => {
      if (Array.isArray(loc) && loc.length >= 2 && isFinite(loc[0]) && isFinite(loc[1])) {
        lats.push(loc[0]);
        lons.push(loc[1]);
      }
    });
    if (!lats.length || !lons.length) {
      throw new Error('Invalid locations data for rectangle calculation.');
    }
    const buffer = 0.01;
    let west = Math.min(...lons) - buffer;
    let east = Math.max(...lons) + buffer;
    let north = Math.max(...lats) + buffer;
    let south = Math.min(...lats) - buffer;

    // Clamp to valid ranges
    const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));
    west = clamp(west, -180, 180);
    east = clamp(east, -180, 180);
    north = clamp(north, -90, 90);
    south = clamp(south, -90, 90);


    return Cesium.Rectangle.fromDegrees(west, south, east, north);
  }
private goToAdHocRegion(rectangle: any): void {
  const viewer = this.globeService.getViewer();

  if (
    !viewer ||
    !viewer.camera ||
    !rectangle ||
    !isFinite(rectangle.west) ||
    !isFinite(rectangle.south) ||
    !isFinite(rectangle.east) ||
    !isFinite(rectangle.north) ||
    rectangle.west === rectangle.east ||
    rectangle.south === rectangle.north ||
    (rectangle.east - rectangle.west) < 0.0001 ||
    (rectangle.north - rectangle.south) < 0.0001
  ) {
    console.error('Invalid rectangle or viewer for flyTo:', rectangle);
    return;
  }

  const destination = Cesium.Rectangle.fromDegrees(
    Cesium.Math.toDegrees(rectangle.west),
    Cesium.Math.toDegrees(rectangle.south),
    Cesium.Math.toDegrees(rectangle.east),
    Cesium.Math.toDegrees(rectangle.north)
  );

  try {
    if (viewer.scene.mode !== Cesium.SceneMode.SCENE3D) {
      viewer.scene.morphTo3D(0);
      viewer.scene.morphComplete.addEventListener(() => {
        viewer.camera.flyTo({ destination });
      });
    } else {
      setTimeout(() => {
        viewer.camera.flyTo({ destination });
      }, 1000);
    }
  } catch (e) {
    console.error('Cesium flyTo error:', e, destination);
    try {
      viewer.camera.setView({ destination });
    } catch (e2) {
      console.error('Cesium setView error:', e2, destination);
    }
  }
}

//procall
  async startProcallPlaybackMode(eventId: string, component: any): Promise<void> {
    this.clearPlaybackMode();

    try {
      const response = await this.illumLiveService.getPlaybackProcall(eventId).toPromise();

      await this.parsePlaybackProcall(eventId, response ?? []);

      if (this.data.playbackProcall && this.data.playbackProcall.timestamp) {
        this.timeline.setTime(this.data.playbackProcall.timestamp);
        this.timeline.pause();
      }
    } catch (error) {
      this.errorModal('Error', 'Not able to retrieve archived Procall', true);
      this.stopPlaybackMode();
      console.error('Error retrieving procalls.', error);
      throw error;
    }
  }

  private async parsePlaybackProcall(eventId: string, procalls: any[]): Promise<void> {
    if (!procalls || !procalls.length) {
      return;
    }

    try {
      const device = await firstValueFrom(this.deviceService.getDeviceForPlaybackProcall(procalls[0]));
      if (!device) {
        this.errorModal('Error', 'No locations found for Procall', true);
        this.stopPlaybackMode();
        return;
      } else {
        this.data.playbackProcall = device;
        this.goToProcallOrActiveTrackingDevice(device);
      }
    } catch (error) {
      console.error('Error getting device for playback procall.', error);
      throw error;
    }
  }

  private goToProcallOrActiveTrackingDevice(device: any): void {
    if (device && device.firstStatus) {
      const viewer = this.core.getViewer();
      if (viewer && viewer.camera && device.firstStatus.longitude && device.firstStatus.latitude) {
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(
            device.firstStatus.longitude,
            device.firstStatus.latitude,
            100.0
          )
        });
      }
    
    }
  }
  private errorModal(title: string, data: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      size: 'md',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = data;

    return modalRef.result;
  }
    removeProcallPlaybackDevice(device: any): void {
      if (device && typeof device.setProcallState === 'function') {
        device.setProcallState(false);
      }
      this.removeDeviceFromMap(device);
      if (device?.procallId && this.procallDevices) {
        delete this.procallDevices[device.procallId];
      }
    }

    private removeDeviceFromMap(device: any, dataSourceKey?: string): void {
      if (device && typeof device.canBeGarbageCollected === 'function' && device.canBeGarbageCollected()) {
        this.dataContext.removeMapEntity(device, false, dataSourceKey);
      }
    }
}
